import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { staggerContainer, staggerItem, instant, tabFade } from "@/lib/animations";
import QuoteCarousel from "@/components/QuoteCarousel";
import { UserButton } from "@clerk/react";
import { safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safeStorage";
import {
  getMyDonations,
  getMyImpact,
  getMyBadges,
  listPools,
  getLeaderboard,
  updateMe,
  type Pool,
} from "@workspace/api-client-react";
import {
  Download, Trophy, Heart, DollarSign, Leaf, Users, Medal,
  Share2, TrendingUp, Clock, Sparkles, Info, FileText, FileCheck,
  ChevronDown, ChevronRight, Loader2, EyeOff, AlertTriangle,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import LanguagePill from "@/components/LanguagePill";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import DonateModal from "@/components/DonateModal";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

const TIER_COLORS: Record<string, { bg: string; text: string; border: string; pill: string }> = {
  bronze:   { bg: "bg-amber-50",   text: "text-amber-900",   border: "border-amber-400",   pill: "bg-amber-100" },
  silver:   { bg: "bg-slate-50",   text: "text-slate-700",   border: "border-slate-400",   pill: "bg-slate-100" },
  gold:     { bg: "bg-yellow-50",  text: "text-yellow-900",  border: "border-yellow-500",  pill: "bg-yellow-100" },
  platinum: { bg: "bg-indigo-50",  text: "text-indigo-900",  border: "border-indigo-400",  pill: "bg-indigo-100" },
  none:     { bg: "bg-muted",      text: "text-muted-foreground", border: "border-muted", pill: "bg-muted" },
};


const TIER_THRESHOLDS: Record<string, number> = {
  bronze: 50, silver: 250, gold: 1000, platinum: 5000,
};

function TierBadgePill({ tier }: { tier: string }) {
  const { t } = useTranslation();
  const c = TIER_COLORS[tier] ?? TIER_COLORS.none;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${c.pill} ${c.text} ${c.border}`}>
      {tier === "none" ? t("donorDashboard.noBadgeYet") : t("donorDashboard.tierDonor", { tier })}
    </span>
  );
}

const DONOR_CATEGORY_OPTIONS = [
  { value: "all",          label: "All Pools" },
  { value: "medical",      label: "Medical" },
  { value: "student",      label: "Student" },
  { value: "credit_card",  label: "Credit & Consumer" },
  { value: "auto",         label: "Auto" },
  { value: "housing",      label: "Housing" },
  { value: "legal",        label: "Legal" },
  { value: "family",       label: "Family" },
  { value: "veterans",     label: "Veterans" },
  { value: "small_business", label: "Small Business" },
  { value: "misc",         label: "Other" },
];

interface PlatformStats {
  totalDebtsCleared: number;
  totalPeopleHelped: number;
  totalDollarsMoved: number;
  activePools: number;
  avgDaysToPayoff: number;
}

type EnrichedPool = Pool & {
  velocityPerDay: number;
  daysToFull: number | null;
};

interface ImpactSummary {
  displayName: string;
  lifetimeDonated: number;
  poolsContributed: { poolId: number; poolName: string; totalGiven: number }[];
  estimatedDebtsCleared: number;
  estimatedAmountCleared: number;
  tier: string;
  nextTier: string | null;
  nextTierAmount: number | null;
  tierProgress: number;
}

async function fetchPlatformStats(): Promise<PlatformStats> {
  const res = await fetch("/api/stats/platform");
  if (!res.ok) throw new Error("Failed to fetch platform stats");
  return res.json() as Promise<PlatformStats>;
}

async function fetchImpactSummary(): Promise<ImpactSummary> {
  const res = await fetch("/api/users/me/impact-summary");
  if (!res.ok) throw new Error("Failed to fetch impact summary");
  return res.json() as Promise<ImpactSummary>;
}

interface TaxCertificate {
  id: number;
  donationId: number | null;
  poolName: string | null;
  amount: number | null;
  hasFile: boolean;
  sentAt: string | null;
  createdAt: string;
  donationDate: string | null;
}

async function fetchMyTaxCertificates(): Promise<TaxCertificate[]> {
  const res = await fetch("/api/donations/my-tax-certificates");
  if (!res.ok) throw new Error("Failed to fetch tax certificates");
  return res.json() as Promise<TaxCertificate[]>;
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/70 border border-green-200 min-w-[120px] flex-1">
      <div className="text-green-600 mb-1">{icon}</div>
      <div className="text-2xl font-bold text-green-900">{value}</div>
      <div className="text-xs text-green-700 font-medium text-center">{label}</div>
      {sub && <div className="text-xs text-green-600 text-center">{sub}</div>}
    </div>
  );
}

function AnimatedProgressBar({ pct, className = "" }: { pct: number; className?: string }) {
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setWidth(Math.min(100, Math.max(0, pct))), 100);
    return () => clearTimeout(t);
  }, [pct]);

  return (
    <div ref={ref} className={`w-full bg-muted rounded-full overflow-hidden ${className}`} style={{ height: "8px" }}>
      <div
        className="bg-primary rounded-full h-full transition-all duration-700 ease-out"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function PoolCard({ pool, onDonate }: { pool: EnrichedPool; onDonate: () => void }) {
  const { t } = useTranslation();
  const velocity = pool.velocityPerDay;
  const daysToFull = pool.daysToFull;

  return (
    <Card className="glass-card flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          {pool.icon && <span className="text-2xl mt-0.5 leading-none">{pool.icon}</span>}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base leading-snug">{pool.name}</CardTitle>
              {pool.category && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border capitalize">
                  {pool.category.replace(/_/g, " ")}
                </span>
              )}
            </div>
            {pool.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{pool.description}</p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 flex-1 flex flex-col">
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="font-semibold text-primary">${pool.balance.toLocaleString()}</span>
            <span className="text-muted-foreground text-xs">{t("donorDashboard.goal")}: ${pool.threshold.toLocaleString()}</span>
          </div>
          <AnimatedProgressBar pct={pool.percentFunded} />
          <div className="flex justify-between items-center mt-1.5">
            <span className="text-xs font-semibold text-primary">{pool.percentFunded}% {t("poolsSection.funded")}</span>
            <span className="text-xs text-muted-foreground">{pool.debtorsInQueue} {t("donorDashboard.debtsInQueue")}</span>
          </div>
        </div>

        {(velocity > 0 || daysToFull) && (
          <div className="flex gap-2 text-xs">
            {velocity > 0 && (
              <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <TrendingUp className="h-3 w-3" />
                ${velocity.toLocaleString(undefined, { maximumFractionDigits: 0 })}/day
              </span>
            )}
            {daysToFull !== null && daysToFull > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                <Clock className="h-3 w-3" />
                ~{daysToFull} {t("donorDashboard.daysToFull")}
              </span>
            )}
          </div>
        )}

        <div className="mt-auto pt-2">
          <Button className="w-full" onClick={onDonate}>
            {t("donorDashboard.donateToPool")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function generateShareCardPng(impact: ImpactSummary): void {
  const canvas = document.createElement("canvas");
  canvas.width = 960;
  canvas.height = 480;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const green = "#16a34a";
  const darkGreen = "#166534";
  const lightGreen = "#f0fdf4";
  const mutedGreen = "#dcfce7";

  ctx.fillStyle = lightGreen;
  ctx.fillRect(0, 0, 960, 480);

  ctx.fillStyle = green;
  ctx.fillRect(0, 0, 960, 90);

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 42px sans-serif";
  ctx.fillText("DebtBridge", 50, 62);
  ctx.font = "18px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.fillText("Debt Relief Foundation", 50, 82);

  ctx.fillStyle = mutedGreen;
  ctx.beginPath();
  ctx.arc(860, 45, 30, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = green;
  ctx.beginPath();
  ctx.moveTo(860, 32); ctx.lineTo(873, 52); ctx.lineTo(847, 52); ctx.closePath();
  ctx.fill();
  ctx.textAlign = "left";

  ctx.fillStyle = darkGreen;
  ctx.font = "bold 52px sans-serif";
  const clearedAmt = impact.estimatedAmountCleared > 0 ? impact.estimatedAmountCleared : impact.lifetimeDonated;
  const headline = `I helped clear $${Math.round(clearedAmt).toLocaleString()} in debt`;
  ctx.fillText(headline, 50, 185);

  ctx.font = "28px sans-serif";
  ctx.fillStyle = green;
  ctx.fillText("through DebtBridge — join me.", 50, 230);

  if (impact.estimatedDebtsCleared > 0) {
    ctx.font = "20px sans-serif";
    ctx.fillStyle = "#4b5563";
    ctx.fillText(
      `That's roughly ${impact.estimatedDebtsCleared} ${impact.estimatedDebtsCleared === 1 ? "person" : "people"} helped get debt-free.`,
      50,
      275,
    );
  }

  ctx.fillStyle = mutedGreen;
  ctx.roundRect(40, 305, 880, 70, 12);
  ctx.fill();

  const tierName = impact.tier === "none" ? "New Donor" : impact.tier.charAt(0).toUpperCase() + impact.tier.slice(1) + " Donor";
  ctx.fillStyle = green;
  ctx.font = "bold 22px sans-serif";
  ctx.fillText(`${tierName} — making a real difference.`, 60, 348);

  ctx.fillStyle = green;
  ctx.font = "bold 20px sans-serif";
  ctx.fillText("debtbridge.org", 50, 440);
  ctx.font = "16px sans-serif";
  ctx.fillStyle = "#6b7280";
  ctx.fillText("100% of donations go directly to clearing real debt.", 200, 440);

  const dataUrl = canvas.toDataURL("image/png");
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = "my-debtbridge-impact.png";
  link.click();
}

export default function DonorDashboard() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const reduce = useReducedMotion();
  const staggerC = reduce ? instant : staggerContainer;
  const staggerI = reduce ? instant : staggerItem;
  const [activeTab, setActiveTab] = useState("donate");
  const [donatePool, setDonatePool] = useState<Pool | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>(() => safeGetItem("donor-category-filter") ?? "all");
  const [sharingLoading, setSharingLoading] = useState(false);
  const [downloadingZipYear, setDownloadingZipYear] = useState<number | null>(null);
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(new Set());
  const [selectedYear, setSelectedYear] = useState<number | null>(() => {
    const stored = safeGetItem("donor-tax-year-filter");
    return stored ? Number(stored) : null;
  });

  const queryClient = useQueryClient();

  const { data: donations, isError: donationsError, error: donationsErr } = useQuery({ queryKey: ["myDonations"], queryFn: getMyDonations });
  const { data: impact, isError: impactError, error: impactErr } = useQuery({ queryKey: ["myImpact"], queryFn: getMyImpact });
  const { data: badges, isError: badgesError, error: badgesErr } = useQuery({ queryKey: ["myBadges"], queryFn: getMyBadges });
  const { data: pools } = useQuery({ queryKey: ["pools"], queryFn: () => listPools() });
  const { data: leaderboard } = useQuery({ queryKey: ["leaderboard"], queryFn: getLeaderboard, refetchInterval: 60000 });

  const toggleAnonymousMutation = useMutation({
    mutationFn: (anon: boolean) => updateMe({ isLeaderboardAnonymous: anon }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["myBadges"] });
      void queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
    },
    onError: () => toast.error("Failed to update privacy setting."),
  });
  const { data: platformStats } = useQuery({
    queryKey: ["platformStats"],
    queryFn: fetchPlatformStats,
    staleTime: 1000 * 60 * 5,
  });
  const { data: impactSummary } = useQuery({
    queryKey: ["impactSummary"],
    queryFn: fetchImpactSummary,
  });
  const { data: taxCertificates } = useQuery({
    queryKey: ["myTaxCertificates"],
    queryFn: fetchMyTaxCertificates,
  });

  useEffect(() => {
    if (selectedYear === null || !taxCertificates) return;
    const availableYears = taxCertificates.map((c) => new Date(c.donationDate ?? c.createdAt).getUTCFullYear());
    if (!availableYears.includes(selectedYear)) setSelectedYear(null);
  }, [taxCertificates, selectedYear]);

  useEffect(() => {
    if (categoryFilter === "all") safeRemoveItem("donor-category-filter");
    else safeSetItem("donor-category-filter", categoryFilter);
  }, [categoryFilter]);

  useEffect(() => {
    if (selectedYear === null) safeRemoveItem("donor-tax-year-filter");
    else safeSetItem("donor-tax-year-filter", String(selectedYear));
  }, [selectedYear]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      toast.success("Thank you for your donation! Your contribution is making a real difference.", { duration: 6000 });
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const activePools = pools?.items ?? [];
  const filteredActivePools = categoryFilter === "all"
    ? activePools
    : activePools.filter((p) => p.category === categoryFilter);

  const myRank = badges?.myRank ?? null;

  function downloadCert(id: number) {
    window.open(`/api/donations/${id}/certificate`, "_blank");
  }

  async function downloadAllForYear(year: number) {
    setDownloadingZipYear(year);
    try {
      const res = await fetch(`/api/donations/my-tax-certificates/zip?year=${year}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Download failed" })) as { error?: string };
        throw new Error(body.error ?? "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `irs-acknowledgment-letters-${year}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Download failed";
      toast.error(`Could not download letters for ${year}: ${msg}`);
    } finally {
      setDownloadingZipYear(null);
    }
  }

  function downloadImpactPdf() {
    window.open("/api/users/me/impact-pdf", "_blank");
  }

  const handleShare = useCallback(() => {
    if (!impactSummary) return;
    setSharingLoading(true);
    try {
      generateShareCardPng(impactSummary);
    } finally {
      setSharingLoading(false);
    }
  }, [impactSummary]);

  const tier = badges?.tier ?? impact?.tier ?? "none";
  const tierColors = TIER_COLORS[tier] ?? TIER_COLORS.none;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "linear-gradient(180deg, #f7fbf5 0%, #faf9f6 100%)" }}>
      {/* Charitable/community — ghost texture at 4%, barely visible warmth */}
      <img
        src="https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&w=1600"
        alt="" aria-hidden loading="lazy"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.1)", pointerEvents: "none" }}
      />
      <nav className="sticky top-0 z-50 border-b bg-gradient-to-r from-emerald-900 to-stone-800 border-emerald-800">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={28} />
            <span className="font-semibold text-white">{t("donorDashboard.portalLabel")}</span>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-amber-950 tracking-wide">
              {t("donorDashboard.roleBadge")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguagePill light />
            <UserButton />
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Role / API error banner — shown when critical donor endpoints return an error */}
        {(donationsError || impactError || badgesError) && (() => {
          const isRoleNotConfirmed = (err: unknown) => {
            if (!err || typeof err !== "object") return false;
            const e = err as Record<string, unknown>;
            return e["status"] === 403 &&
              typeof e["data"] === "object" && e["data"] !== null &&
              (e["data"] as Record<string, unknown>)["code"] === "ROLE_NOT_CONFIRMED";
          };
          const roleError = isRoleNotConfirmed(donationsErr) || isRoleNotConfirmed(impactErr) || isRoleNotConfirmed(badgesErr);
          return (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                {roleError ? (
                  <>
                    <p className="font-semibold">Your account role needs to be confirmed</p>
                    <p className="mt-0.5 text-amber-800">
                      Sign out and sign back in to complete your role confirmation.{" "}
                      <a href="/select-role" className="underline font-medium hover:no-underline">
                        Confirm now →
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">Some data couldn't be loaded</p>
                    <p className="mt-0.5 text-amber-800">
                      Please refresh the page. If the problem persists, try signing out and back in.
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* Live Platform Stats Bar */}
        {!platformStats ? (
          <div className="rounded-2xl bg-muted/60 p-5 mb-8">
            <div className="flex flex-wrap gap-3">
              {[0,1,2,3,4].map((i) => <Skeleton key={i} className="h-20 flex-1 min-w-[120px] rounded-xl" />)}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-gradient-to-r from-green-700 to-emerald-600 p-5 mb-8 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold uppercase tracking-wide">{t("donorDashboard.livePlatformImpact")}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <StatCard
                icon={<Users className="h-5 w-5" />}
                label={t("donorDashboard.peopleHelped")}
                value={platformStats.totalPeopleHelped.toLocaleString()}
              />
              <StatCard
                icon={<DollarSign className="h-5 w-5" />}
                label={t("donorDashboard.dollarsMoved")}
                value={`$${platformStats.totalDollarsMoved >= 1000
                  ? `${(platformStats.totalDollarsMoved / 1000).toFixed(1)}K`
                  : platformStats.totalDollarsMoved.toLocaleString()}`}
              />
              <StatCard
                icon={<Heart className="h-5 w-5" />}
                label={t("donorDashboard.debtsCleared")}
                value={platformStats.totalDebtsCleared.toLocaleString()}
              />
              <StatCard
                icon={<Leaf className="h-5 w-5" />}
                label={t("donorDashboard.activePools")}
                value={platformStats.activePools.toLocaleString()}
              />
              <StatCard
                icon={<Clock className="h-5 w-5" />}
                label={t("donorDashboard.avgDaysToPayoff")}
                value={platformStats.avgDaysToPayoff > 0 ? `${platformStats.avgDaysToPayoff}` : "N/A"}
                sub={t("donorDashboard.submissionToCleared")}
              />
            </div>
          </div>
        )}

        {/* Hero Impact Banner */}
        {!impact ? (
          <div className="rounded-2xl border-2 border-muted p-6 mb-8 flex flex-col md:flex-row items-center gap-6 bg-muted/30">
            <Skeleton className="w-28 h-28 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-3 w-full">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-14 w-24" />
              <Skeleton className="h-4 w-56" />
            </div>
            <div className="flex md:flex-col gap-4 md:gap-3">
              <Skeleton className="w-28 h-16 rounded-xl" />
              <Skeleton className="w-28 h-16 rounded-xl" />
            </div>
          </div>
        ) : (
          <div className={`rounded-2xl border-2 p-6 mb-8 flex flex-col md:flex-row items-center gap-6 ${tierColors.bg} ${tierColors.border}`}>

            {badges?.svgBadge ? (
              <div className="flex-shrink-0 flex flex-col items-center gap-2">
                <img
                  className="w-28 h-28 drop-shadow-md"
                  src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(badges.svgBadge)}`}
                  alt={`${tier} donor badge`}
                />
                <TierBadgePill tier={tier} />
              </div>
            ) : (
              <div className="flex-shrink-0 flex flex-col items-center gap-2">
                <div className="w-28 h-28 rounded-full bg-muted/60 flex items-center justify-center"><Leaf className="h-12 w-12 text-muted-foreground/50" /></div>
                <TierBadgePill tier="none" />
              </div>
            )}

            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                <Users className="h-5 w-5 text-green-700" />
                <span className="text-sm font-medium text-green-800 uppercase tracking-wide">{t("donorDashboard.yourImpact")}</span>
              </div>
              <div className="text-5xl md:text-6xl font-extrabold text-green-900 leading-tight">
                {impact.debtsErased}
              </div>
              <div className="text-lg font-semibold text-green-800 mt-1">
                {impact.debtsErased === 1 ? t("donorDashboard.personHelped") : t("donorDashboard.peopleHelpedDebtFree")}
              </div>
              <div className="text-sm text-green-700 mt-1">
                {t("donorDashboard.youveErased")} <span className="font-semibold">${impact.amountErased.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> {t("donorDashboard.inDebt")}
              </div>
            </div>

            <div className="flex md:flex-col gap-4 md:gap-3 text-center">
              <div className={`rounded-xl px-4 py-3 border ${tierColors.border} bg-white/60`}>
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <DollarSign className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs text-muted-foreground">{t("donorDashboard.totalGiven")}</span>
                </div>
                <div className="text-xl font-bold">${impact.totalDonated.toLocaleString()}</div>
              </div>
              <div className={`rounded-xl px-4 py-3 border ${tierColors.border} bg-white/60`}>
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Medal className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs text-muted-foreground">{t("donorDashboard.leaderboard")}</span>
                </div>
                <div className="text-xl font-bold">
                  {myRank ? `#${myRank}` : "—"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tier Progress Bar */}
        {!badges ? (
          <div className="mb-8 px-1">
            <div className="flex justify-between items-center mb-1.5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ) : badges.nextTier && badges.nextTierAmount && (
          <div className="mb-8 px-1">
            <div className="flex justify-between items-center mb-1.5 text-sm">
              <span className="font-medium capitalize">{TIER_EMOJI[tier] ?? ""} {tier === "none" ? "No tier yet" : `${tier} tier`}</span>
              <span className="text-muted-foreground">
                ${(badges.nextTierAmount - badges.totalDonated).toLocaleString()} more to unlock{" "}
                <span className="font-semibold capitalize">{TIER_EMOJI[badges.nextTier] ?? ""} {badges.nextTier}</span>
              </span>
            </div>
            <AnimatedProgressBar pct={Math.min(100, (badges.totalDonated / badges.nextTierAmount) * 100)} />
          </div>
        )}

        <div>
          {/* Animated tab bar with sliding indicator */}
          <div
            role="tablist"
            className="flex items-center bg-muted p-1 rounded-lg mb-6 overflow-x-auto"
            style={{ position: "relative", gap: 0 }}
          >
            {[
              { value: "donate",      label: t("donorDashboard.tabDonate") },
              { value: "impact",      label: t("donorDashboard.tabImpact") },
              { value: "history",     label: t("donorDashboard.tabHistory") },
              { value: "tax-docs",    label: t("donorDashboard.tabTaxDocs") },
              { value: "badge",       label: t("donorDashboard.tabBadge") },
              { value: "leaderboard", label: t("donorDashboard.tabLeaderboard") },
            ].map((tab) => (
              <button
                key={tab.value}
                role="tab"
                aria-selected={activeTab === tab.value}
                onClick={() => setActiveTab(tab.value)}
                style={{
                  position: "relative",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: activeTab === tab.value ? 500 : 400,
                  color: activeTab === tab.value ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s ease",
                  fontFamily: "inherit",
                  flexShrink: 0,
                }}
              >
                {activeTab === tab.value && !reduce && (
                  <motion.div
                    layoutId="donor-tab-indicator"
                    className="absolute inset-0 rounded-md bg-background shadow-sm"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
                <span style={{ position: "relative", zIndex: 1 }}>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Animated tab content */}
          <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            initial={reduce ? false : tabFade.initial}
            animate={reduce ? {} : tabFade.animate}
            exit={reduce ? {} : tabFade.exit}
          >

          {/* Donate Tab */}
          {activeTab === "donate" && (<>
            <div className="flex flex-wrap gap-2 mb-5">
              {DONOR_CATEGORY_OPTIONS.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategoryFilter(cat.value)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                    categoryFilter === cat.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-border hover:border-primary hover:text-foreground"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              {t("donorDashboard.activePoolsCount", { filtered: filteredActivePools.length, total: activePools.length })}
            </div>
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={staggerC}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.05 }}
            >
              {filteredActivePools.length === 0 ? (
                <div className="col-span-3 text-center py-12 text-muted-foreground">
                  {activePools.length === 0 ? t("donorDashboard.noActivePools") : t("donorDashboard.noPoolsInCategory")}
                </div>
              ) : (
                filteredActivePools.map((pool) => (
                  <motion.div key={pool.id} variants={staggerI}>
                    <PoolCard pool={pool as EnrichedPool} onDonate={() => setDonatePool(pool)} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </>)}

          {/* My Impact Tab */}
          {activeTab === "impact" && (<>
            {impactSummary ? (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-3 gap-4">
                  <Card className="glass-card">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <DollarSign className="h-8 w-8 text-primary mb-2" />
                      <div className="text-3xl font-bold text-primary">${impactSummary.lifetimeDonated.toLocaleString()}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t("donorDashboard.lifetimeDonated")}</div>
                    </CardContent>
                  </Card>
                  <Card className="glass-card">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <Users className="h-8 w-8 text-green-600 mb-2" />
                      <div className="text-3xl font-bold text-green-700">{impactSummary.estimatedDebtsCleared}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t("donorDashboard.estDebtsCleared")}</div>
                    </CardContent>
                  </Card>
                  <Card className="glass-card">
                    <CardContent className="pt-6 flex flex-col items-center text-center">
                      <Heart className="h-8 w-8 text-rose-500 mb-2" />
                      <div className="text-3xl font-bold text-rose-600">{impactSummary.poolsContributed.length}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t("donorDashboard.poolsSupported")}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Pools contributed to */}
                {impactSummary.poolsContributed.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">{t("donorDashboard.poolsYouveSupported")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {impactSummary.poolsContributed.map((p) => (
                          <div key={p.poolId} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="font-medium text-sm">{p.poolName}</span>
                            <span className="text-sm text-primary font-semibold">${p.totalGiven.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Badge tier progress */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t("donorDashboard.donorTierProgress")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full border rounded-lg divide-y text-sm mb-4">
                      {(["seedling", "sapling", "tree", "oak", "forest"] as const).map((tier) => {
                        const threshold = TIER_THRESHOLDS[tier] ?? 0;
                        const isEarned = impactSummary.lifetimeDonated >= threshold;
                        const isCurrent = impactSummary.tier === tier;
                        return (
                          <div key={tier} className={`flex items-center justify-between px-3 py-2.5 ${isCurrent ? "bg-primary/5 font-semibold" : ""}`}>
                            <span className={`capitalize flex items-center gap-1.5 ${isEarned ? "text-foreground" : "text-muted-foreground"}`}>
                              {TIER_EMOJI[tier]} {tier}
                            </span>
                            <span className={isEarned ? "text-green-600 font-medium" : "text-muted-foreground"}>
                              {isEarned ? t("donorDashboard.earned") : `$${threshold.toLocaleString()}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {impactSummary.nextTier && impactSummary.nextTierAmount && (
                      <>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Progress to {TIER_EMOJI[impactSummary.nextTier]} {impactSummary.nextTier}</span>
                          <span>{impactSummary.tierProgress}%</span>
                        </div>
                        <AnimatedProgressBar pct={impactSummary.tierProgress} />
                        <div className="text-xs text-muted-foreground mt-1">
                          ${(impactSummary.nextTierAmount - impactSummary.lifetimeDonated).toLocaleString()} more to unlock {impactSummary.nextTier}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button onClick={downloadImpactPdf} variant="outline" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {t("donorDashboard.downloadImpactPdf")}
                  </Button>
                  <Button
                    onClick={handleShare}
                    disabled={sharingLoading || impactSummary.lifetimeDonated === 0}
                    className="flex items-center gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    {t("donorDashboard.shareImpact")}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/tax-info")}>
                    <Info className="h-4 w-4 mr-1" />
                    {t("donorDashboard.taxDeductionInfo")}
                  </Button>
                </div>

                <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40 border">
                  <strong>Note:</strong> "Estimated debts helped clear" is a proportional estimate based on your share of total platform donations. Actual debt clearances depend on pool funding, eligibility, and platform operations.
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">{t("donorDashboard.loadingImpact")}</div>
            )}
          </>)}

          {/* History Tab */}
          {activeTab === "history" && (
            <Card>
              <CardHeader>
                <CardTitle>{t("donorDashboard.donationHistory")}</CardTitle>
              </CardHeader>
              <CardContent>
                {!donations ? (
                  <div className="space-y-3">
                    {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
                  </div>
                ) : donations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">{t("donorDashboard.noDonationsYet")}</div>
                ) : (
                  <div className="space-y-3">
                    {donations.map((d) => (
                      <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <div className="font-medium">${Number(d.amount).toLocaleString()} to {d.poolName}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(d.createdAt).toLocaleDateString()} ·{" "}
                            {d.isRecurring ? t("donorDashboard.monthly") : t("donorDashboard.oneTime")} ·{" "}
                            <span className={`capitalize ${d.status === "completed" ? "text-green-600" : "text-muted-foreground"}`}>
                              {d.status}
                            </span>
                          </div>
                        </div>
                        {d.status === "completed" && (
                          <Button variant="outline" size="sm" onClick={() => downloadCert(d.id)}>
                            <Download className="h-4 w-4 mr-1.5" /> {t("donorDashboard.taxCert")}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tax Documents Tab */}
          {activeTab === "tax-docs" && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-primary" />
                  <CardTitle>{t("donorDashboard.taxDocuments")}</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("donorDashboard.taxDocumentsDesc")}
                </p>
              </CardHeader>
              <CardContent>
                {!taxCertificates ? (
                  <div className="text-center py-8 text-muted-foreground">{t("donorDashboard.loadingTaxDocs")}</div>
                ) : taxCertificates.length === 0 ? (
                  <div className="text-center py-10">
                    <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground font-medium">{t("donorDashboard.noTaxCertsYet")}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t("donorDashboard.certsIssuedAutomatically")}
                    </p>
                  </div>
                ) : (() => {
                  const byYear = taxCertificates.reduce<Record<number, typeof taxCertificates>>((acc, cert) => {
                    const dateStr = cert.donationDate ?? cert.createdAt;
                    const year = new Date(dateStr).getUTCFullYear();
                    if (!acc[year]) acc[year] = [];
                    acc[year].push(cert);
                    return acc;
                  }, {});
                  const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);
                  const visibleYears = selectedYear === null ? years : years.filter((y) => y === selectedYear);
                  return (
                    <div className="space-y-4">
                      {years.length > 1 && (
                        <div className="flex items-center gap-2">
                          <label htmlFor="tax-year-filter" className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                            {t("donorDashboard.filterByYear")}
                          </label>
                          <select
                            id="tax-year-filter"
                            value={selectedYear ?? ""}
                            onChange={(e) => {
                              const yr = e.target.value === "" ? null : Number(e.target.value);
                              setSelectedYear(yr);
                              if (yr !== null) {
                                setCollapsedYears((prev) => {
                                  const next = new Set(prev);
                                  next.delete(yr);
                                  return next;
                                });
                              }
                            }}
                            className="h-8 rounded-md border border-input bg-background px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <option value="">{t("donorDashboard.allYears")}</option>
                            {years.map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {visibleYears.map((year) => {
                        const isCollapsed = collapsedYears.has(year);
                        const certs = byYear[year];
                        return (
                          <div key={year} className="rounded-lg border overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-primary/5 hover:bg-primary/10 transition-colors">
                              <button
                                onClick={() =>
                                  setCollapsedYears((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(year)) next.delete(year);
                                    else next.add(year);
                                    return next;
                                  })
                                }
                                className="flex items-center gap-2.5 text-left flex-1 min-w-0"
                              >
                                <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-0.5 text-sm font-semibold text-primary ring-1 ring-primary/20">
                                  {year} {t("donorDashboard.taxYear")}
                                </span>
                                <span className="text-xs text-muted-foreground">{certs.length} certificate{certs.length !== 1 ? "s" : ""}</span>
                                {isCollapsed
                                  ? <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                                  : <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto" />
                                }
                              </button>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={downloadingZipYear === year}
                                onClick={(e) => { e.stopPropagation(); void downloadAllForYear(year); }}
                                className="ml-3 flex-shrink-0 text-xs"
                              >
                                {downloadingZipYear === year
                                  ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> {t("donorDashboard.preparing")}</>
                                  : <><Download className="h-3.5 w-3.5 mr-1" /> {t("donorDashboard.downloadAll")}</>
                                }
                              </Button>
                            </div>
                            {!isCollapsed && (
                              <div className="divide-y">
                                {certs.map((cert) => (
                                  <div key={cert.id} className="flex items-center justify-between px-4 py-3 bg-muted/10 hover:bg-muted/20 transition-colors">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <FileCheck className="h-5 w-5 text-green-600 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-medium text-sm">
                                            {t("donorDashboard.taxCertificate", { id: cert.id })}
                                          </span>
                                          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-blue-200">
                                            {year} {t("donorDashboard.taxYear")}
                                          </span>
                                          {cert.poolName && (
                                            <span className="text-muted-foreground text-xs font-normal">— {cert.poolName}</span>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                          {cert.amount != null && (
                                            <span className="font-medium text-foreground">${cert.amount.toLocaleString()} </span>
                                          )}
                                          {t("donorDashboard.issued")} {new Date(cert.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                                        </div>
                                      </div>
                                    </div>
                                    {cert.hasFile ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.open(`/api/donations/letters/${cert.id}/download`, "_blank")}
                                        className="flex-shrink-0 ml-3"
                                      >
                                        <Download className="h-4 w-4 mr-1.5" /> {t("donorDashboard.downloadPdf")}
                                      </Button>
                                    ) : cert.donationId != null ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.open(`/api/donations/${cert.donationId}/certificate`, "_blank")}
                                        className="flex-shrink-0 ml-3"
                                      >
                                        <Download className="h-4 w-4 mr-1.5" /> {t("donorDashboard.downloadPdf")}
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-muted-foreground ml-3 flex-shrink-0">{t("donorDashboard.pdfNotAvailable")}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Badge Tab */}
          {activeTab === "badge" && (<>
            {badges ? (
              <div className="max-w-sm mx-auto">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-center">{t("donorDashboard.yourDonorBadge")}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center gap-5">
                    <img
                      className="w-36 h-36 drop-shadow-lg"
                      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(badges.svgBadge)}`}
                      alt={`${badges.tier} donor badge`}
                    />
                    <TierBadgePill tier={badges.tier} />
                    <div className="text-sm text-center text-muted-foreground">
                      <span className="font-semibold text-foreground">${badges.totalDonated.toLocaleString()}</span> {t("donorDashboard.totalDonated")}
                    </div>

                    <div className="w-full border rounded-lg divide-y text-sm">
                      {(["bronze", "silver", "gold", "platinum"] as const).map((tier) => {
                        const threshold = TIER_THRESHOLDS[tier] ?? 0;
                        const isEarned = badges.totalDonated >= threshold;
                        const isCurrent = badges.tier === tier;
                        return (
                          <div key={tier} className={`flex items-center justify-between px-3 py-2 ${isCurrent ? "bg-primary/5 font-semibold" : ""}`}>
                            <span className={`capitalize flex items-center gap-1.5 ${isEarned ? "text-foreground" : "text-muted-foreground"}`}>
                              {TIER_EMOJI[tier]} {tier}
                            </span>
                            <span className={isEarned ? "text-green-600" : "text-muted-foreground"}>
                              {isEarned ? t("donorDashboard.earned") : `$${threshold.toLocaleString()}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {badges.nextTier && badges.nextTierAmount && (
                      <div className="w-full text-sm">
                        <div className="flex justify-between mb-1">
                          <span className="text-muted-foreground">Progress to {TIER_EMOJI[badges.nextTier]} {badges.nextTier}</span>
                          <span className="font-medium">${badges.nextTierAmount.toLocaleString()}</span>
                        </div>
                        <AnimatedProgressBar pct={Math.min(100, (badges.totalDonated / badges.nextTierAmount) * 100)} />
                        <div className="text-xs text-muted-foreground mt-1">
                          ${(badges.nextTierAmount - badges.totalDonated).toLocaleString()} more to unlock {badges.nextTier} tier
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">Loading badge…</div>
            )}
          </>)}

          {/* Leaderboard Tab */}
          {activeTab === "leaderboard" && (
            <div className="space-y-4">
              {/* Anonymity toggle */}
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="anon-toggle" className="font-medium cursor-pointer">
                          {t("donorDashboard.anonymousOnLeaderboard")}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("donorDashboard.hiddenFromLeaderboard")}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="anon-toggle"
                      checked={badges?.isLeaderboardAnonymous ?? false}
                      onCheckedChange={(checked) => toggleAnonymousMutation.mutate(checked)}
                      disabled={toggleAnonymousMutation.isPending}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" /> {t("donorDashboard.leaderboardTitle")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!leaderboard ? (
                    <div className="space-y-2">
                      {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
                    </div>
                  ) : leaderboard.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">No donors yet. Be the first!</div>
                  ) : (
                    <div className="space-y-2">
                      {leaderboard.map((entry) => {
                        const isMe = entry.isCurrentUser ?? false;
                        const isOutsideTop = isMe && entry.rank > 20;
                        return (
                          <div key={`${entry.rank}-${entry.displayName}`}>
                            {isOutsideTop && (
                              <div className="flex items-center gap-2 my-2 text-xs text-muted-foreground">
                                <div className="flex-1 border-t border-dashed" />
                                <span>{t("donorDashboard.yourRank")}</span>
                                <div className="flex-1 border-t border-dashed" />
                              </div>
                            )}
                            <div
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                isMe
                                  ? "border-primary/50 bg-primary/5 ring-1 ring-primary/20"
                                  : "hover:bg-muted/30"
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                                entry.rank === 1 ? "bg-yellow-100 text-yellow-700" :
                                entry.rank === 2 ? "bg-gray-100 text-gray-600" :
                                entry.rank === 3 ? "bg-amber-100 text-amber-700" :
                                isMe ? "bg-primary/10 text-primary" :
                                "bg-muted text-muted-foreground"
                              }`}>
                                {entry.rank}
                              </div>
                              {entry.svgBadge ? (
                                <img
                                  className="w-8 h-8 flex-shrink-0"
                                  src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(entry.svgBadge)}`}
                                  alt={`${entry.tier} badge`}
                                />
                              ) : null}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate flex items-center gap-1">
                                  {entry.displayName}
                                  {isMe && <span className="text-xs font-normal text-primary">(you)</span>}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Heart className="h-3 w-3 text-rose-400" />
                                  {entry.debtsErased} {entry.debtsErased === 1 ? "debt" : "debts"} erased
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="font-medium text-primary">${entry.totalDonated.toLocaleString()}</div>
                                <TierBadgePill tier={entry.tier} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <QuoteCarousel />

      {donatePool && (
        <DonateModal pool={donatePool} onClose={() => setDonatePool(null)} />
      )}
    </div>
  );
}
